import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;
const SWIPE_START_THRESHOLD = 9;
const SWIPE_AXIS_DOMINANCE = 1.18;
const SWIPE_CLOSE_RATIO = 0.27;
const SWIPE_CLOSE_MIN_DISTANCE = 30;
const SWIPE_CLOSE_VELOCITY = 0.52;
const SWIPE_SETTLE_DURATION = 220;
const CLICK_SUPPRESSION_DURATION = 420;
const SWIPE_HARD_IGNORE_SELECTOR = [
  '[data-drawer-swipe-ignore]',
  'input',
  'textarea',
  'select',
  'option',
  '[contenteditable="true"]',
  'iframe',
  'video[controls]',
  'shopify-accelerated-checkout',
  'shopify-accelerated-checkout-cart',
  'shopify-payment-terms',
].join(', ');
const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * A drawer that opens from the right side.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); we install a focus trap via
 * `trapFocus()` so Tab cycles within the drawer, mirroring the modal-mode
 * a11y contract. Focus moves to the close button on open and returns to
 * the trigger on close.
 *
 * On narrow viewports (< 990px) the drawer overlays with a backdrop. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Same focus-on-close-button + restore-on-close UX.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {{ id: number, startX: number, startY: number, lastX: number, lastTime: number, velocityX: number, axis: 'pending' | 'horizontal' | 'vertical' | 'ignored', distance: number, width: number, direction: 1 | -1 } | null} */
  #swipePointer = null;

  /** @type {number | undefined} */
  #swipeTimer;

  /** @type {number | undefined} */
  #swipeFrame;

  /** @type {{ distance: number, width: number, direction: 1 | -1 } | null} */
  #pendingSwipeRender = null;

  /** @type {number} */
  #suppressClickUntil = 0;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);
    this.#setupSwipeClose();

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.#teardownSwipeClose();
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;
  }

  #setupSwipeClose() {
    if (!this.hasAttribute('data-swipe-close')) return;

    const { panel } = this.refs;
    panel.addEventListener('pointerdown', this.#onSwipePointerDown, true);
    panel.addEventListener('pointermove', this.#onSwipePointerMove, { passive: false, capture: true });
    panel.addEventListener('pointerup', this.#onSwipePointerEnd, true);
    panel.addEventListener('pointercancel', this.#onSwipePointerEnd, true);
    panel.addEventListener('click', this.#onSwipeClickCapture, true);
  }

  #teardownSwipeClose() {
    if (!this.hasAttribute('data-swipe-close')) return;

    const { panel } = this.refs;
    panel.removeEventListener('pointerdown', this.#onSwipePointerDown, true);
    panel.removeEventListener('pointermove', this.#onSwipePointerMove, true);
    panel.removeEventListener('pointerup', this.#onSwipePointerEnd, true);
    panel.removeEventListener('pointercancel', this.#onSwipePointerEnd, true);
    panel.removeEventListener('click', this.#onSwipeClickCapture, true);
    this.#resetSwipeClose();
  }

  /**
   * Resolve the physical direction in which the panel should be dismissed.
   * inline-end means right in LTR and left in RTL.
   * @returns {1 | -1}
   */
  #getSwipeDirection() {
    const inlineDirection = this.getAttribute('data-swipe-close') || 'inline-end';
    const isRtl = getComputedStyle(this).direction === 'rtl';

    if (inlineDirection === 'inline-start') return /** @type {1 | -1} */ (isRtl ? 1 : -1);
    return /** @type {1 | -1} */ (isRtl ? -1 : 1);
  }

  /** @param {PointerEvent} event */
  #onSwipePointerDown = (event) => {
    if (!this.isOpen || !this.#isTopmost() || !event.isPrimary || event.button !== 0 || event.pointerType === 'mouse') {
      return;
    }
    if (this.#hasOpenNestedDialog() || !(event.target instanceof Element)) return;
    // Nearly the full panel can originate a swipe. Taps, links, buttons,
    // quantity controls and accordions remain native until a clear horizontal
    // drag wins the axis lock; only text-entry and embedded payment UI are hard exclusions.
    if (event.target.closest(SWIPE_HARD_IGNORE_SELECTOR)) return;

    this.#resetSwipeClose();
    this.#swipePointer = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastTime: performance.now(),
      velocityX: 0,
      axis: 'pending',
      distance: 0,
      width: 0,
      direction: this.#getSwipeDirection(),
    };
  };

  /** @param {PointerEvent} event */
  #onSwipePointerMove = (event) => {
    const pointer = this.#swipePointer;
    if (!pointer || pointer.id !== event.pointerId || !this.isOpen) return;

    const direction = pointer.direction;
    const dx = event.clientX - pointer.startX;
    const dy = event.clientY - pointer.startY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const closingDistance = dx * direction;

    if (pointer.axis === 'pending' && Math.hypot(dx, dy) >= SWIPE_START_THRESHOLD) {
      if (absY > absX * SWIPE_AXIS_DOMINANCE) {
        pointer.axis = 'vertical';
      } else if (absX > absY * SWIPE_AXIS_DOMINANCE) {
        pointer.axis = closingDistance > 0 ? 'horizontal' : 'ignored';
      }
    }

    if (pointer.axis !== 'horizontal') return;
    if (event.cancelable) event.preventDefault();

    const now = performance.now();
    const elapsed = Math.max(1, now - pointer.lastTime);
    pointer.velocityX = (event.clientX - pointer.lastX) / elapsed;
    pointer.lastX = event.clientX;
    pointer.lastTime = now;

    const { panel } = this.refs;
    if (pointer.width === 0) pointer.width = Math.max(1, panel.getBoundingClientRect().width);
    const width = pointer.width;
    pointer.distance = Math.min(width, Math.max(0, closingDistance));

    if (!panel.hasPointerCapture(event.pointerId)) {
      try {
        panel.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is an enhancement; the gesture still works without it.
      }
    }

    panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');
    this.#queueSwipeClose(pointer.distance, width, direction);
  };

  /** @param {PointerEvent} event */
  #onSwipePointerEnd = (event) => {
    const pointer = this.#swipePointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    this.#swipePointer = null;

    const { panel } = this.refs;
    if (panel.hasPointerCapture(event.pointerId)) {
      try {
        panel.releasePointerCapture(event.pointerId);
      } catch {
        // The browser may already have released capture on pointercancel.
      }
    }

    if (pointer.axis !== 'horizontal') return;

    const direction = pointer.direction;
    const width = pointer.width || Math.max(1, panel.getBoundingClientRect().width);
    const closeDistance = Math.min(96, width * SWIPE_CLOSE_RATIO);
    const directedVelocity = pointer.velocityX * direction;
    const shouldClose =
      event.type !== 'pointercancel' &&
      (pointer.distance >= closeDistance ||
        (pointer.distance >= SWIPE_CLOSE_MIN_DISTANCE && directedVelocity >= SWIPE_CLOSE_VELOCITY));

    if (pointer.distance >= SWIPE_START_THRESHOLD) {
      this.#suppressClickUntil = performance.now() + CLICK_SUPPRESSION_DURATION;
    }

    this.#renderSwipeClose(pointer.distance, width, direction);
    this.#settleSwipeClose(shouldClose, direction);
  };

  /** @param {MouseEvent} event */
  #onSwipeClickCapture = (event) => {
    if (performance.now() >= this.#suppressClickUntil) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  /**
   * Coalesce high-frequency pointer events into one compositor-friendly write per frame.
   * @param {number} distance
   * @param {number} width
   * @param {1 | -1} direction
   */
  #queueSwipeClose(distance, width, direction) {
    this.#pendingSwipeRender = { distance, width, direction };
    if (this.#swipeFrame !== undefined) return;

    this.#swipeFrame = requestAnimationFrame(() => {
      this.#swipeFrame = undefined;
      const pending = this.#pendingSwipeRender;
      this.#pendingSwipeRender = null;
      if (pending) this.#renderSwipeClose(pending.distance, pending.width, pending.direction);
    });
  }

  #cancelSwipeFrame() {
    if (this.#swipeFrame !== undefined) cancelAnimationFrame(this.#swipeFrame);
    this.#swipeFrame = undefined;
    this.#pendingSwipeRender = null;
  }

  /**
   * @param {number} distance
   * @param {number} width
   * @param {1 | -1} direction
   */
  #renderSwipeClose(distance, width, direction) {
    const progress = Math.min(1, distance / Math.max(1, width));
    const { panel } = this.refs;

    panel.classList.add('theme-drawer__dialog--swipe-dragging');
    panel.style.setProperty('transition', 'none');
    panel.style.setProperty('transform', `translate3d(${direction * distance}px, 0, 0)`);
    panel.style.setProperty('--aw-drawer-swipe-progress', `${progress}`);
  }

  /**
   * @param {boolean} close
   * @param {1 | -1} direction
   */
  #settleSwipeClose(close, direction) {
    this.#cancelSwipeFrame();
    const { panel } = this.refs;
    const duration = REDUCED_MOTION_QUERY.matches ? 0 : SWIPE_SETTLE_DURATION;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

    panel.classList.remove('theme-drawer__dialog--swipe-dragging');
    panel.style.setProperty('transition', `transform ${duration}ms ${easing}`);
    panel.style.setProperty('transform', close ? `translate3d(${direction * 100}%, 0, 0)` : 'translate3d(0, 0, 0)');
    panel.style.setProperty('--aw-drawer-swipe-progress', close ? '1' : '0');

    this.#clearSwipeTimer();
    this.#swipeTimer = window.setTimeout(() => {
      this.#swipeTimer = undefined;
      if (close) {
        panel.classList.add('theme-drawer__dialog--swipe-dismissed');
        this.close();
      } else {
        this.#resetSwipeClose();
      }
    }, duration + 24);
  }

  #clearSwipeTimer() {
    if (this.#swipeTimer === undefined) return;
    window.clearTimeout(this.#swipeTimer);
    this.#swipeTimer = undefined;
  }

  #resetSwipeClose() {
    this.#clearSwipeTimer();
    this.#cancelSwipeFrame();
    this.#swipePointer = null;

    const { panel } = this.refs;
    panel.classList.remove('theme-drawer__dialog--swipe-dragging', 'theme-drawer__dialog--swipe-dismissed');
    panel.style.removeProperty('transition');
    panel.style.removeProperty('transform');
    panel.style.removeProperty('--aw-drawer-swipe-progress');
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop so this handler is inert.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.panel.querySelector('dialog[open]'));
  }

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    this.#resetSwipeClose();
    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place.
    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else {
      this.#focusInitialElement();
    }

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (!this.isOpen) this.#resetSwipeClose();

    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;
    const swipeDismissed = panel.classList.contains('theme-drawer__dialog--swipe-dismissed');

    if (!swipeDismissed) this.#resetSwipeClose();

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    unlockScroll(panel);

    if (panel.open) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      if (!swipeDismissed) {
        panel.classList.add('theme-drawer__dialog--closing');
        await onAnimationEnd(panel, undefined, { subtree: false });
        panel.classList.remove('theme-drawer__dialog--closing');
      }
    }

    panel.close();
    this.#resetSwipeClose();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
